import React, { useContext } from 'react';
import { Alert, Form, Input, InputNumber, Select, Space, Switch, Tag } from 'antd';
import { message } from '@/components/Message';
import {
  SKU,
  SKUWrapper,
  Specification,
  StandardProduct,
  useAddOrUpdateSku,
} from '@/models/sku';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { produce } from 'immer';
import { BarcodeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import ModalSelect from '../ModalSelect';
import { NumericKeyboardInput } from '../NumericKeyboard';
import PosButton from '../PosButton';

const formLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  labelAlign: 'right' as 'right',
};

export interface SpToGoodsFormProps {
  product: StandardProduct & Pick<SKU, 'isOnSale' | 'isStoreOnSale' | 'categoryId'>;
  onSuccess: (skuWrapper: SKUWrapper) => void;
  fillSpecificationInfo: boolean;
  onCancel?: () => void;
}
const SpToGoodsForm: React.FC<SpToGoodsFormProps> = ({
  product,
  onSuccess = () => {},
  onCancel = () => {},
  fillSpecificationInfo,
}: SpToGoodsFormProps) => {
  const [form] = Form.useForm();
  const [{ categoryMap = {} }] = useContext(BaseInfoContext);
  const categoryMapEntries = Object.entries(categoryMap);

  const { send: addToGoods, loading: addToGoodsLoading } = useAddOrUpdateSku();

  const initialValues = produce(product, draft => {
    draft.gallery = draft.gallery && draft.gallery.length ? [draft.gallery[0]] : [];
    draft.isStoreOnSale = true;
    draft.isOnSale = false;
    draft.categoryId = 0;
  });

  const onSubmit = values => {
    let skuWrapper = {} as SKUWrapper;
    skuWrapper.goods = {
      spId: product.id,
      goodsSn: product.barcode,
      name: values.productName,
      categoryId: values.categoryId,
      isStoreOnSale: values.isStoreOnSale,
      isOnSale: values.isOnSale,
    } as SKU;
    skuWrapper.specifications = [
      {
        name: product.type || '',

        price: values.price,
        stock: values.stock || 0,
        cost: values.cost || 0,
      } as Specification,
    ];

    addToGoods(skuWrapper)
      .then(v => {
        onSuccess(v);
      })
      .catch(e => {
        message.error('添加失败:' + e.msg);
      });
  };

  return (
    <>
      {!product.id ? (
        <Alert
          showIcon
          message={'未能从中央商品库查找到该商品信息，请手动录入'}
          type={'error'}
          style={{ marginBottom: 15 }}
        />
      ) : null}
      <Form form={form} initialValues={initialValues} onFinish={onSubmit}>
        <Form.Item label={'条码'} {...formLayout}>
          <Tag icon={<BarcodeOutlined />}>{product.barcode}</Tag>
        </Form.Item>
        <Form.Item
          required
          label={'商品名'}
          name={'productName'}
          rules={[{ required: true, message: '请填写商品名' }]}
          {...formLayout}>
          <Input />
        </Form.Item>

        {categoryMapEntries.length ? (
          <Form.Item required label="上架品类" name={'categoryId'} {...formLayout}>
            <ModalSelect style={{ width: 200 }} playSoundOnSelect>
              <Select.Option value={0}>默认品类</Select.Option>
              {categoryMapEntries.map(([id, name]) => (
                <Select.Option key={id} value={+id}>
                  {name}
                </Select.Option>
              ))}
            </ModalSelect>
          </Form.Item>
        ) : null}

        <Form.Item required label={'售价'} {...formLayout}>
          <Form.Item name={'price'} noStyle rules={[{ required: true, message: '请填写售价' }]}>
            <NumericKeyboardInput
              emptyValueOnOpen={true}
              inputType={InputNumber}
              inputProps={{
                placeholder: '￥',
                style: { width: 150 },
                min: 0,
                precision: 2,
              }}
              keyboardProps={{
                parseValueToNumber: true,
                precision: 2,
                showAllClean: true,
                placement: 'bottomLeft',
                trigger: 'click',
                style: { width: 300 },
              }}
            />
          </Form.Item>{' '}
          元
        </Form.Item>

        {fillSpecificationInfo && (
          <>
            <Form.Item
              required
              label={'入库数量'}
              rules={[{ required: true, message: '请填写入库数量' }]}
              name={'stock'}
              {...formLayout}>
              <NumericKeyboardInput
                emptyValueOnOpen={true}
                inputType={InputNumber}
                inputProps={{
                  placeholder: '入库数量',
                  style: { width: 150 },
                  min: 0,
                  max: 999999999,
                  precision: 0,
                }}
                keyboardProps={{
                  parseValueToNumber: true,
                  precision: 0,
                  showAllClean: true,
                  placement: 'bottomLeft',
                  trigger: 'click',
                  style: { width: 300 },
                }}
              />
            </Form.Item>

            <Form.Item label={'成本价'} extra={'用于利润计算'} {...formLayout}>
              <Form.Item name={'cost'} noStyle>
                <NumericKeyboardInput
                  emptyValueOnOpen={true}
                  inputType={InputNumber}
                  inputProps={{
                    placeholder: '￥',
                    style: { width: 150 },
                    min: 0,
                    precision: 2,
                  }}
                  keyboardProps={{
                    parseValueToNumber: true,
                    precision: 2,
                    showAllClean: true,
                    placement: 'bottomLeft',
                    trigger: 'click',
                    style: { width: 300 },
                  }}
                />
              </Form.Item>{' '}
              元
            </Form.Item>
          </>
        )}
        <Form.Item
          label="门店上架"
          valuePropName="checked"
          extra="未上架的商品将不会在 POS 机展示"
          name={'isStoreOnSale'}
          {...formLayout}>
          <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
        </Form.Item>

        <Form.Item
          label="小程序上架"
          valuePropName="checked"
          extra="未上架的商品将不会在小程序展示"
          name={'isOnSale'}
          {...formLayout}>
          <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
        </Form.Item>

        <Form.Item
          wrapperCol={{ offset: formLayout.labelCol.span, span: formLayout.wrapperCol.span }}>
          <Space>
            <PosButton
              loading={addToGoodsLoading}
              onClick={() => {
                form.submit();
              }}
              className="form-main-submit"
              type="primary">
              确定
            </PosButton>
            <PosButton
              loading={addToGoodsLoading}
              onClick={() => {
                onCancel();
              }}
              className="form-main-submit">
              取消
            </PosButton>
          </Space>
        </Form.Item>
      </Form>
    </>
  );
};

export default SpToGoodsForm;
